import * as React from "react";
import type { Editor } from "@tiptap/react";
import { CellSelection, TableMap, findTable, cellAround } from "prosemirror-tables";

type BorderKind = "row-top" | "col-left";

const BTN = 24;          // tamanho do botão
const GAP = 6;           // espaço entre os botões
const PAD = 8;           // padding da área clicável (p/ "congelar")
const IN_OFFSET = 6;     // deslocamento para DENTRO da borda
const BORDER_TOL = 10;   // tolerância para considerar "na borda"
const CENTER_TOL = 12;   // tolerância para considerar "no centro" da célula

export default function TableEdgeControls({ editor }: { editor: Editor | null | undefined }) {
    const [ui, setUI] = React.useState<null | {
        tablePos: number;
        row: number;
        col: number;
        mapW: number;
        mapH: number;
        kind: BorderKind;
        cluster: { x: number; y: number; w: number; h: number };
        plusHit: { x: number; y: number; w: number; h: number };
        trashHit: { x: number; y: number; w: number; h: number };
    }>(null);

    const freezeRef = React.useRef(false);
    const lastPoint = React.useRef({ x: 0, y: 0 });

    React.useEffect(() => {
        if (!editor) return;
        const { view } = editor;
        let raf = 0;

        const inside = (x: number, y: number, r: { x: number; y: number; w: number; h: number }) =>
            x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;

        const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

        const computeAt = (clientX: number, clientY: number) => {
            const res = view.posAtCoords({ left: clientX, top: clientY });
            if (!res) { setUI(null); return; }

            const $pos = view.state.doc.resolve(res.pos);
            const table = findTable($pos);
            if (!table) { setUI(null); return; }

            const $cell = cellAround($pos);
            if (!$cell) { setUI(null); return; }

            const map = TableMap.get(table.node);
            const start = table.pos + 1;
            const rect = map.findCell($cell.pos - start);

            // DOMs/boxes
            const cellPos = start + map.positionAt(rect.top, rect.left, table.node);
            const cellDom = view.nodeDOM(cellPos) as HTMLElement | null;
            const tableDom = view.nodeDOM(table.pos) as HTMLElement | null;
            if (!cellDom || !tableDom) { setUI(null); return; }

            const cellBox = cellDom.getBoundingClientRect();
            const tb = (tableDom as HTMLElement).getBoundingClientRect();

            // centro da célula sob o mouse (viewport)
            const cxCell = (cellBox.left + cellBox.right) / 2;
            const cyCell = (cellBox.top + cellBox.bottom) / 2;

            // distâncias até bordas da CÉLULA
            const dLeft = Math.abs(clientX - cellBox.left);
            const dTop = Math.abs(clientY - cellBox.top);

            // distâncias até o "centro" da célula
            const dCenterX = Math.abs(clientX - cxCell);
            const dCenterY = Math.abs(clientY - cyCell);

            // candidatos SOMENTE nas bordas primárias E perto do CENTRO
            const cand: Array<{ kind: BorderKind; dist: number }> = [];
            if (rect.top === 0 && dTop <= BORDER_TOL && dCenterX <= CENTER_TOL) {
                cand.push({ kind: "row-top", dist: dCenterX });
            }
            if (rect.left === 0 && dLeft <= BORDER_TOL && dCenterY <= CENTER_TOL) {
                cand.push({ kind: "col-left", dist: dCenterY });
            }
            if (!cand.length) { setUI(null); return; }

            // pega o candidato mais "central"
            const best = cand.reduce((a, b) => (a.dist < b.dist ? a : b));
            const kind = best.kind;

            // cluster (dois botões lado a lado, estável)
            const CLUSTER_W = BTN * 2 + GAP;
            const CLUSTER_H = BTN;

            let cx = 0, cy = 0; // posição do cluster (top-left, coords de viewport)
            if (kind === "row-top") {
                // centraliza horizontalmente no centro da célula; Y levemente DENTRO do topo da tabela
                cx = clamp(cxCell - CLUSTER_W / 2, tb.left + 6, tb.right - CLUSTER_W - 6);
                cy = tb.top + IN_OFFSET;
            } else { // col-left
                // X levemente DENTRO da esquerda da tabela; centraliza verticalmente no centro da célula
                cx = tb.left + IN_OFFSET;
                cy = clamp(cyCell - CLUSTER_H / 2, tb.top + 6, tb.bottom - CLUSTER_H - 6);
            }

            const plusHit = { x: cx - PAD, y: cy - PAD, w: BTN + PAD * 2, h: BTN + PAD * 2 };
            const trashHit = { x: cx + BTN + GAP - PAD, y: cy - PAD, w: BTN + PAD * 2, h: BTN + PAD * 2 };

            setUI({
                tablePos: table.pos,
                row: rect.top,
                col: rect.left,
                mapW: map.width,
                mapH: map.height,
                kind,
                cluster: { x: cx, y: cy, w: CLUSTER_W, h: CLUSTER_H },
                plusHit,
                trashHit,
            });
        };

        const onMouseMove = (e: MouseEvent) => {
            lastPoint.current = { x: e.clientX, y: e.clientY };

            // congela enquanto o mouse estiver sobre as áreas clicáveis
            if (ui && (inside(e.clientX, e.clientY, ui.plusHit) || inside(e.clientX, e.clientY, ui.trashHit))) {
                freezeRef.current = true;
                return;
            } else {
                freezeRef.current = false;
            }

            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => computeAt(e.clientX, e.clientY));
        };

        const onScroll: EventListener = () => {
            if (freezeRef.current) return;
            const p = lastPoint.current;
            if (p.x || p.y) computeAt(p.x, p.y);
        };
        const onResize: EventListener = onScroll;

        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("scroll", onScroll, true);
        window.addEventListener("resize", onResize);

        return () => {
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("scroll", onScroll, true);
            window.removeEventListener("resize", onResize);
            cancelAnimationFrame(raf);
        };
    }, [editor, ui]);

    if (!editor || !ui) return null;

    // ---------- helpers de seleção ----------
    function getCtxFromTablePos(tablePos: number) {
        const { state } = editor!;
        const posInside = Math.min(tablePos + 1, state.doc.content.size);
        const $inside = state.doc.resolve(posInside);
        const table = findTable($inside) || findTable(state.selection.$from);
        if (!table) return null;
        const map = TableMap.get(table.node);
        const start = table.pos + 1;
        return { table, map, start };
    }

    function selectRowAt(rowIndex: number) {
        const data = ui;
        if (!editor || !data) return false;
        const { state, view } = editor;
        const ctx = getCtxFromTablePos(data.tablePos);
        if (!ctx) return false;
        const { table, map, start } = ctx;
        const r = Math.max(0, Math.min(rowIndex, map.height - 1));
        const a = start + map.positionAt(r, 0, table.node);
        const b = start + map.positionAt(r, map.width - 1, table.node);
        view.dispatch(state.tr.setSelection(CellSelection.rowSelection(state.doc.resolve(a), state.doc.resolve(b))));
        view.focus();
        return true;
    }

    function selectColAt(colIndex: number) {
        const data = ui;
        if (!editor || !data) return false;
        const { state, view } = editor;
        const ctx = getCtxFromTablePos(data.tablePos);
        if (!ctx) return false;
        const { table, map, start } = ctx;
        const c = Math.max(0, Math.min(colIndex, map.width - 1));
        const a = start + map.positionAt(0, c, table.node);
        const b = start + map.positionAt(map.height - 1, c, table.node);
        view.dispatch(state.tr.setSelection(CellSelection.colSelection(state.doc.resolve(a), state.doc.resolve(b))));
        view.focus();
        return true;
    }

    // ---------- ações ----------
    // ADICIONAR: sempre DEPOIS da linha/coluna atuais (da célula sob o mouse)
    const addAtBorder = () => {
        const data = ui;
        if (!editor || !data) return;
        const { kind, row, col } = data;

        if (kind === "col-left") {
            if (!selectRowAt(row)) return;
            editor.chain().focus().addRowAfter().run();
        } else {
            if (!selectColAt(col)) return;
            editor.chain().focus().addColumnAfter().run();
        }
    };

    // REMOVER: remove exatamente a linha/coluna atuais
    const delAtBorder = () => {
        const data = ui;
        if (!editor || !data) return;
        const { kind, row, col } = data;

        if (kind === "col-left") {
            if (!selectRowAt(row)) return;
            editor.can().deleteRow() && editor.chain().focus().deleteRow().run();
        } else {
            if (!selectColAt(col)) return;
            editor.can().deleteColumn() && editor.chain().focus().deleteColumn().run();
        }
    };

    const stopDown: React.MouseEventHandler = (e) => { e.preventDefault(); e.stopPropagation(); };

    // ---------- render ----------
    return (
        <div style={{ position: "fixed", inset: 0, zIndex: 9999, pointerEvents: "none" }}>
            <div
                style={{
                    position: "fixed",
                    left: ui.cluster.x,
                    top: ui.cluster.y,
                    width: ui.cluster.w,
                    height: ui.cluster.h,
                    display: "flex",
                    gap: GAP,
                    pointerEvents: "auto",
                }}
            >
                <button
                    className="tt-btn"
                    title={ui.kind === "col-left" ? "Adicionar coluna" : "Adicionar linha"}
                    onMouseDown={stopDown}
                    onClick={addAtBorder}
                    style={{ borderRadius: 999, width: BTN, height: BTN, lineHeight: `${BTN - 2}px` }}
                >
                    +
                </button>
                <button
                    className="tt-btn tt-danger"
                    title={ui.kind === "col-left" ? "Remover coluna" : "Remover linha"}
                    onMouseDown={stopDown}
                    onClick={delAtBorder}
                    style={{ borderRadius: 999, width: BTN, height: BTN, lineHeight: `${BTN - 2}px` }}
                >
                    –
                </button>
            </div>
        </div>
    );
}
